import { useState, useEffect, useMemo } from 'react';
import { Form, Modal } from 'antd';
import { ExclamationCircleOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axiosInstance from '../../../variables/api/axiosInstance';
import axiosMultiPartDataInstance from '../../../variables/api/axiosMultiPartDatainstance';
import { recupererLesContratsActifs, recupererLesEntitesDeconstat } from '../../../hooks/useHooks';
import { formaterLesContrats, formaterLesEntites, getEnginsFromContrat } from '../../../variables/functions';
import { normalizeParametersOptions } from '../../../variables/stringFunctions';
import { buildAutoVictimes } from '../../../pages/sinistres/components/declaration/VictimesSection';

const useDeclarationSinistre = () => {
    const [form] = Form.useForm();
    const [currentStep, setCurrentStep] = useState(0);
    const [formData, setFormData] = useState({});
    const [isAssureConducteur, setIsAssureConducteur] = useState(true);

    const [adversaires, setAdversaires] = useState([]);
    const [adversairePrincipalId, setAdversairePrincipalId] = useState(null);
    const [victimes, setVictimes] = useState([]);

    const [typeSinistre, setTypeSinistre] = useState('MATERIEL');
    const [selectedGaranties, setSelectedGaranties] = useState([]);
    const [garanties, setGaranties] = useState([]);
    const [evaluationInitiale, setEvaluationInitiale] = useState(0);

    const [entites, setEntites] = useState([]);
    const [contratsLoading, setContratsLoading] = useState(false);
    const [contratsData, setContratsData] = useState([]);
    const [engins, setEngins] = useState([]);
    const [selectedContratObj, setSelectedContratObj] = useState(null);
    const [codeCategorie, setCodeCategorie] = useState('');

    const [loading, setLoading] = useState(false);
    const [theContract, setTheContract] = useState('');
    const [cModal, setCModal] = useState(false);
    const [cityOptions, setCityOptions] = useState([]);
    const [quartiersData, setQuartiersData] = useState([]);
    const [communesData, setCommunesData] = useState([]);
    const [loadingQuartiers, setLoadingQuartiers] = useState(false);
    const [selectedLocation, setSelectedLocation] = useState(null);
    const [sequenceSinistre, setSequenceSinistre] = useState('');
    const [assuranceOptions, setAssuranceOptions] = useState([]);
    const [professions, setProfessions] = useState([]);

    const navigate = useNavigate();

    const autoVictimes = useMemo(
        () => buildAutoVictimes(formData, isAssureConducteur, adversaires, selectedContratObj),
        [formData, isAssureConducteur, adversaires, selectedContratObj]
    );

    // Chargements initiaux
    useEffect(() => {
        searchContrats('');
        recupererLesEntitesDeconstat().then(r => setEntites(formaterLesEntites(r.data))).catch(() => setEntites([]));

        axiosInstance.get('/api/parametre/type/VILLE').then(res => {
            const opts = (res.data || []).map(item => {
                const v = item?.libelleParametre || item?.label || item?.value || '';
                return v ? { value: v.trim(), label: v.trim() } : null;
            }).filter(Boolean).sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setCityOptions(opts);
        }).catch(() => { });

        axiosInstance.get('/api/organisme/assurance').then(res => {
            const opts = (Array.isArray(res.data) ? res.data : [])
                .map(it => ({ value: it?.raisonSociale, label: it?.raisonSociale }))
                .sort((a, b) => a.label.localeCompare(b.label, 'fr', { sensitivity: 'base' }));
            setAssuranceOptions(opts);
        }).catch(() => setAssuranceOptions([]));

        axiosInstance.get('/api/parametre/type/PROFESSION').then(res => setProfessions(normalizeParametersOptions(res.data))).catch(() => { });
        loadLocations();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!codeCategorie) return;
        axiosInstance.get(`/api/sinistre/next-sequence/${codeCategorie}`).then(res => setSequenceSinistre(res.data)).catch(() => { });
    }, [codeCategorie]);

    useEffect(() => {
        if (!formData?.contratCodeId) return;
        axiosInstance.get(`/api/contrat/preview/${formData.contratCodeId}`).then(response => {
            const engine = response?.data?.engins?.find(e => e.immatriculation === formData?.enginId);
            const g = [];
            engine?.compulsoryGuaranties?.forEach(ga => {
                if (!ga?.nom.toLowerCase().startsWith('surprime')) {
                    g.push({ value: ga.nom, label: ga.nom, garantie: ga, code: ga.nom.startsWith('RC') ? 'RC' : formatGarantieCode(ga.nom) });
                }
            });
            setGaranties(g);
        }).catch(() => { });
    }, [formData?.contratCodeId]);

    const searchContrats = async (searchValue) => {
        setContratsLoading(true);
        try {
            const response = await recupererLesContratsActifs(searchValue);
            setContratsData(formaterLesContrats(response.data.content));
        } catch { setContratsData([]); }
        finally { setContratsLoading(false); }
    };

    const loadLocations = async () => {
        setLoadingQuartiers(true);
        try {
            const [r1, r2] = await Promise.all([fetch('/data/regions.json'), fetch('/data/communes.json')]);
            const regionsData = await r1.json();
            const communesQuartiersData = await r2.json();
            const communesIndex = new Map();
            regionsData.forEach(region => {
                region.prefectures?.forEach(prefecture => {
                    prefecture.communes?.forEach(commune => {
                        communesIndex.set(commune.id, {
                            id: commune.id, nom: commune.nom,
                            prefecture: { id: prefecture.id, nom: prefecture.nom },
                            region: { id: region.id, code: region.code, nom: region.nom }
                        });
                    });
                });
            });
            const allQuartiers = [], allCommunes = [];
            communesQuartiersData.forEach(commune => {
                const cwr = communesIndex.get(commune.id);
                if (cwr) {
                    allCommunes.push(cwr);
                    commune.quartiers?.forEach(q => allQuartiers.push({ id: q.id, nom: q.nom, commune: cwr }));
                }
            });
            setQuartiersData(allQuartiers);
            setCommunesData(allCommunes);
        } catch { setQuartiersData([]); setCommunesData([]); }
        finally { setLoadingQuartiers(false); }
    };

    const formatGarantieCode = (value) => {
        if (!value) return '';
        value = value.trim();
        if (value.length <= 3) return value;
        return value.split(/\s+/).map(w => w[0].toUpperCase()).join('');
    };

    const handleQuartierSelect = (id) => {
        const q = quartiersData.find(x => x.id === id);
        if (q) {
            setSelectedLocation({ type: 'quartier', quartier: q, commune: q.commune, prefecture: q.commune?.prefecture, region: q.commune?.region });
            form.setFieldsValue({ lieuSinistre: q.nom, quartier: q.nom, commune: undefined });
        }
    };

    const handleCommuneSelect = (id) => {
        const c = communesData.find(x => x.id === id);
        if (c) {
            setSelectedLocation({ type: 'commune', commune: c, prefecture: c.prefecture, region: c?.region });
            form.setFieldsValue({ lieuSinistre: c.nom, commune: c.nom, quartier: undefined });
        }
    };

    const calculateEvaluation = () => {
        const tauxResponsabilite = form.getFieldValue('tauxResponsabilite') ?? 100;
        const evalu = {
            nombreBlessesLegers: parseInt(form.getFieldValue('nbBlessesLegers') || 0),
            nombreBlessesMoyens: parseInt(form.getFieldValue('nbBlessesMoyens') || 0),
            nombreBlessesGraves: parseInt(form.getFieldValue('nbBlessesGraves') || 0),
            nombreDeces: parseInt(form.getFieldValue('nbDeces') || 0),
            typeDegats: form.getFieldValue('typeDegatsMateriels') === 'CORPOREL' ? null : form.getFieldValue('typeDegatsMateriels'),
        };
        axiosInstance.post('/api/bareme-sinistre/evaluation', evalu).then(res => {
            setEvaluationInitiale(res.data * (tauxResponsabilite / 100));
            form.setFieldsValue({ evaluationInitiale: res.data });
        }).catch(() => { });
    };

    const next = async () => {
        try {
            const values = await form.validateFields();
            setFormData(prev => ({ ...prev, ...values }));
            setCurrentStep(s => s + 1);
        } catch { /* validation */ }
    };

    const prev = () => setCurrentStep(s => s - 1);

    const handleSubmit = () => {
        Modal.confirm({
            title: 'Confirmer la déclaration de sinistre',
            icon: <ExclamationCircleOutlined />,
            content: 'Êtes-vous sûr de vouloir enregistrer cette déclaration ?',
            okText: 'Confirmer', cancelText: 'Annuler',
            onOk: saveSinistre,
        });
    };

    const saveSinistre = () => {
        setLoading(true);
        const fd = new FormData();
        const photos = formData?.photos;
        const croquis = formData?.croquis;
        const positionsAdversaires = formData?.positionVehiculesAdversaires || {};
        const dataObject = {
            ...formData,
            adversaires: adversaires.map(a => ({
                ...a, estPrincipal: a.id === adversairePrincipalId,
                positionActuelle: positionsAdversaires[String(a.id)] || null,
            })),
            victimes: [...autoVictimes, ...victimes],
            garanties: selectedGaranties,
            heureSurvenance: formData?.heureSurvenance?.format('HH:mm') || null,
            photos: null, croquis: null,
            quartier: selectedLocation?.quartier?.nom,
            prefecture: selectedLocation?.prefecture?.nom,
            region: selectedLocation?.region?.nom,
            commune: selectedLocation?.commune?.nom,
        };
        fd.append('data', new Blob([JSON.stringify(dataObject)], { type: 'application/json' }));
        const appendFiles = (files, key) => files?.fileList?.forEach(f => {
            if (f.originFileObj) fd.append(key, f.originFileObj, f.name);
            else if (f.file) fd.append(key, f.file, f.name);
        });
        appendFiles(photos, 'photos');
        appendFiles(croquis, 'croquis');

        axiosMultiPartDataInstance.post('/api/sinistre/web-new', fd)
            .then(response => {
                if (response.data) {
                    navigate('/details-sinistre', { replace: true, state: { codeId: response.data.codeId } });
                    Modal.success({ title: 'Déclaration enregistrée', content: 'Enregistrée avec succès !' });
                } else Modal.error({ title: 'Erreur', content: 'Une erreur est survenue.' });
            })
            .catch(e => Modal.error({ title: 'Erreur', content: e?.response?.data?.message || 'Une erreur est survenue.' }))
            .finally(() => setLoading(false));
    };

    const handleContractPreview = () => {
        axiosInstance.get(`/api/contrat/codeId/${selectedContratObj?.codeId}`)
            .then(r => { setTheContract(r.data); setCModal(true); }).catch(() => { });
    };

    const handleContratChange = (_, obj) => {
        setSelectedContratObj(obj.contrat);
        setEngins(getEnginsFromContrat(obj.contrat));
    };

    const handleDateSurvenanceChange = (value) => {
        if (value) setCodeCategorie(selectedContratObj?.codeCat);
    };

    const supprimerAdversaire = (id) => {
        const remaining = adversaires.filter(a => a.id !== id);
        setAdversaires(remaining);
        if (adversairePrincipalId === id) setAdversairePrincipalId(remaining.length > 0 ? remaining[0].id : null);
    };

    return {
        form, currentStep, setCurrentStep, formData, isAssureConducteur, setIsAssureConducteur,
        adversaires, setAdversaires, adversairePrincipalId, setAdversairePrincipalId,
        victimes, setVictimes, autoVictimes,
        typeSinistre, setTypeSinistre, selectedGaranties, setSelectedGaranties,
        garanties, evaluationInitiale, setEvaluationInitiale,
        entites, contratsLoading, contratsData, engins,
        selectedContratObj, codeCategorie,
        loading, theContract, cModal, setCModal,
        cityOptions, quartiersData, communesData, loadingQuartiers, selectedLocation,
        sequenceSinistre, assuranceOptions, professions,
        searchContrats, handleQuartierSelect, handleCommuneSelect,
        calculateEvaluation, next, prev, handleSubmit, handleContractPreview,
        handleContratChange, handleDateSurvenanceChange, supprimerAdversaire,
    };
};

export default useDeclarationSinistre;
